import React, { useRef } from 'react';
import loadable from '@loadable/component';
import {
  Countdown,
  defaultMapStyles,
  saleEndsWithinCountdownPeriod,
} from '@secretescapes/cauldron';
import {
  FormattedText,
  IMapOptionsProps,
  IMarkerProps,
  ScoreSize,
  StaticMap,
  Typography,
  TypographyVariant,
  defaultHtmlMapping,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { useAppState } from '@utils/useAppState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useTranslations } from '@utils/useTranslations';
import LazyLoad from 'react-lazyload';
import { AvailabilityPresentation } from '@features/AvailabilityPresentation';
import { AvailabilityPresentationHandle } from '@features/AvailabilityPresentation/AvailabilityPresentation.interfaces';
import { notEmpty } from '@features/AvailabilityPresentation/AvailabilityPresentation.utils';
import { Gallery } from '@features/Gallery';
import { Score } from '@features/Reviews';
import { PropertyHighlightsBadges } from '@features/Reviews/PropertyHighlightsBadges';
import { AssistedSaleBanner } from './AssistedSaleBanner';
import { Badges } from './Badges';
import { Breadcrumbs } from './Breadcrumbs';
import { DetailsSection, UncontrolledDetails } from './Details/UncontrolledDetails';
import { Facilities, FacilitiesWarning } from './Facilities';
import { MaintenanceBox } from './MaintenanceBox';
import { CustomRef, Navigation, SectionId, StickyNavigation } from './Navigation';
import { OmnibusDirective } from './OmnibusDirective';
import { PayPalMessages } from './PayPal/PayPalMessages';
import { RecommendedSales } from './RecommendedSales';
import { SalePageProps } from './SalePage.interfaces';
import {
  AvailabilityPresentationSectionWrapper,
  BaseSectionsContainer,
  BreadcrumbsContainer,
  ContentContainer,
  DetailsContainer,
  FacilitiesContainer,
  HighlightsContainer,
  InformationalContainer,
  KeyInformationContainer,
  LocationAndWhatsNearbyWrapper,
  OmnibusDirectiveWrapper,
  PropertyDetailsWrapper,
  TitleBadgesContainer,
} from './SalePageMobile.styles';
import { TitleSection } from './TitleSection';
import { useScroll } from './useScroll';

const AvailabilityPresentationWrapper = loadable(
  () =>
    import(
      /* webpackChunkName: "AvailabilityPresentationWrapper" */ '../../features/AvailabilityPresentation/SelectionContext'
    ),
  {
    resolveComponent: components => components.SelectionProvider,
    ssr: false,
  }
);

export const DEFAULT_ICON_PNG_URL =
  'http://d1x3cbuht6sy0f.cloudfront.net/assets/images/mobile/map/marker3.png';

export const SalePageMobile = ({
  saleId,
  endDate,
  images,
  staticMapSrc,
  googleMapsKey,
  woosmapApiKey,
  mapProvider = 'google',
  saleTitle,
  destinationName,
  location,
  locale,
  mapAddress,
  facilities,
  hotelDetails,
  highlightsText,
  mainParagraph,
  summary,
  badges,
  isFavourite,
  discount,
  discountTooltip,
  showPricingAndAvailability,
  pricingRules,
  isTimeLimited,
  flightPriceAvailable,
  showReviews,
  reviews,
  refetchFavourites,
  maintenance,
  showAssistedSaleBanner,
  isFamilySale,
  isExpiredSale,
  previewMode,
}: SalePageProps) => {
  const {
    score,
    scoreDescription,
    backgroundColor,
    textColor,
    totalReviewsCount,
    badges: propertyHighlights,
  } = reviews ?? {};
  const { showUrgentDiscount } = pricingRules;
  const { latitude, longitude } = location ?? {};
  const { isOutageOngoing, outageEndDate } = maintenance ?? {};

  const { finalRates } = useAppState();
  const { translations } = useTranslations();

  const stickyRef = useRef<HTMLDivElement | null>(null);
  const locationRef = useRef<CustomRef>({ id: SectionId.Location, display: true });
  const offersRef = useRef<CustomRef>({ id: SectionId.Offers, display: true });
  const availabilityPresentationRef = useRef<AvailabilityPresentationHandle | null>(null);

  const { scrollToRef, scrollToSection } = useScroll(stickyRef);

  const markers: IMarkerProps[] = [
    {
      id: '1',
      markerIconUrl: DEFAULT_ICON_PNG_URL,
      position: { lat: latitude ?? 0, lng: longitude ?? 0 },
    },
  ];

  const mapOptions: IMapOptionsProps = {
    styles: defaultMapStyles,
  };

  const isExtendedPeriod = useFeatureFlag(BullfinchFeatureFlags.extendedPeriodCountdown);

  const displayCountdown =
    !isExpiredSale && isTimeLimited && saleEndsWithinCountdownPeriod(endDate, isExtendedPeriod);

  const hasFacilities = facilities.flattenedFacilities.length > 0;
  const displayFacilities = previewMode || hasFacilities;

  const handleLocationClick = () => {
    scrollToSection(locationRef.current, finalRates.show);
  };

  const scrollToCalendarView = (isStickyNavInactive?: boolean) => {
    if (availabilityPresentationRef.current) {
      scrollToRef(availabilityPresentationRef.current.container, 'top', isStickyNavInactive);
    }
  };

  const handleNavigationCtaClick = () => {
    if (finalRates.show) {
      scrollToSection(offersRef.current, true);
    } else {
      scrollToCalendarView();
      availabilityPresentationRef.current?.openCalendar();
      if (!performance.getEntriesByName('start-render')?.length) {
        performance.mark('start-render');
      }
    }
  };

  return (
    <>
      {isExpiredSale && (
        <LazyLoad once={true}>
          <RecommendedSales isExpiredSale={true} saleIds={[saleId]} isMobile={true} />
        </LazyLoad>
      )}
      {location && (
        <BreadcrumbsContainer>
          <Breadcrumbs location={location} />
        </BreadcrumbsContainer>
      )}
      {images.length > 0 && (
        <Gallery
          images={images}
          saleId={saleId}
          saleTitle={saleTitle}
          isFavourite={!!isFavourite}
          isMobile={true}
          showUrgentDiscount={!!showUrgentDiscount}
          discount={discount}
          discountTooltip={discountTooltip}
          refetchFavourites={refetchFavourites}
        />
      )}
      <ContentContainer>
        <KeyInformationContainer>
          <TitleBadgesContainer>
            <TitleSection
              title={saleTitle}
              subtitle={destinationName}
              onClick={handleLocationClick}
            />
            {badges.length || displayCountdown || showReviews ? (
              <InformationalContainer>
                <Badges badges={badges} isMobile={true} />
                {displayCountdown && <Countdown endDate={endDate} />}
                {showReviews && score ? (
                  <Score
                    score={score}
                    backgroundColor={backgroundColor ?? ''}
                    textColor={textColor ?? ''}
                    scoreDescription={scoreDescription ?? ''}
                    reviewsCount={totalReviewsCount ?? 0}
                    size={ScoreSize.small}
                  />
                ) : null}
              </InformationalContainer>
            ) : null}
          </TitleBadgesContainer>
        </KeyInformationContainer>
        <StickyNavigation
          stickyRef={stickyRef}
          navigation={<Navigation stickyRef={stickyRef} onCtaClick={handleNavigationCtaClick} />}
        >
          <BaseSectionsContainer>
            <PropertyDetailsWrapper>
              <Typography variant={TypographyVariant.t4SansBold}>
                {i18n('sale.hotelDetails.label', [], translations)}
              </Typography>
              {showReviews && propertyHighlights?.length ? (
                <PropertyHighlightsBadges
                  propertyHighlights={propertyHighlights.filter(notEmpty)}
                />
              ) : null}
              <FormattedText html={summary} />
              <UncontrolledDetails
                popupTitle={i18n('sale.hotelDetails.label', [], translations)}
                buttonLabel={i18n('sales.filter.showmore', [], translations)}
                content={
                  <DetailsContainer>
                    <DetailsSection description={mainParagraph} id="mainParagraph" />
                    <DetailsSection description={hotelDetails} id="hotelDetails" />
                    <DetailsSection
                      header={i18n('sale.weLike.title', [], translations)}
                      description={highlightsText}
                      id="weLike"
                    />
                  </DetailsContainer>
                }
              />
            </PropertyDetailsWrapper>
            {displayFacilities && (
              <FacilitiesContainer>
                <Typography variant={TypographyVariant.t4SansBold}>
                  {i18n('facilities_title', [], translations)}
                </Typography>
                {hasFacilities ? <Facilities facilities={facilities} /> : <FacilitiesWarning />}
              </FacilitiesContainer>
            )}
            <LocationAndWhatsNearbyWrapper
              ref={el => (locationRef.current.element = el ?? undefined)}
            >
              <Typography variant={TypographyVariant.t4SansBold}>
                {i18n('sale.locationAndNearby.title', [], translations)}
              </Typography>
              {staticMapSrc && (
                <div data-testid="static-map" id="map-wrapper">
                  <StaticMap
                    id={mapProvider === 'woosmap' ? 'woosmap-static-map' : 'falcon-map'}
                    woosmapApiKey={woosmapApiKey}
                    provider={mapProvider}
                    staticMapSrc={staticMapSrc}
                    googleMapsKey={googleMapsKey}
                    googleMapsLanguage={locale}
                    googleMapsRegion={locale}
                    markers={markers}
                    address={mapAddress}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    options={mapOptions}
                  />
                </div>
              )}
              <HighlightsContainer>
                <FormattedText
                  html={highlightsText}
                  htmlMapping={{
                    ...defaultHtmlMapping,
                    a: TypographyVariant.linkSecondary,
                  }}
                />
              </HighlightsContainer>
              {showAssistedSaleBanner && (
                <AssistedSaleBanner isFamilyVariant={isFamilySale} isMobile={true} />
              )}
            </LocationAndWhatsNearbyWrapper>
            {showPricingAndAvailability && (
              <AvailabilityPresentationSectionWrapper
                id={SectionId.Offers}
                ref={el => (offersRef.current.element = el ?? undefined)}
              >
                {isOutageOngoing ? (
                  <MaintenanceBox endDate={outageEndDate ?? ''} isMobile={false} />
                ) : (
                  <>
                    <Typography variant={TypographyVariant.t4SansBold}>
                      {i18n('sale.offersAndAvailability.title', [], translations)}
                    </Typography>
                    <AvailabilityPresentationWrapper>
                      <PayPalMessages textSize={14} isMobile={true} />
                      <AvailabilityPresentation
                        flightPriceAvailable={flightPriceAvailable}
                        scrollToSection={scrollToCalendarView}
                        ref={availabilityPresentationRef}
                      />
                    </AvailabilityPresentationWrapper>
                  </>
                )}
              </AvailabilityPresentationSectionWrapper>
            )}
            <OmnibusDirectiveWrapper>
              <OmnibusDirective isMobile={true} />
            </OmnibusDirectiveWrapper>
          </BaseSectionsContainer>
        </StickyNavigation>
      </ContentContainer>
      {!isExpiredSale && (
        <LazyLoad once={true}>
          <RecommendedSales
            isExpiredSale={false}
            title={'sale.recommendations'}
            saleIds={[saleId]}
            isMobile={true}
          />
        </LazyLoad>
      )}
    </>
  );
};
